import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { getSafeRandomId } from "@/lib/random-id";
import {
  aggregateSessionsByDay,
  getDateKey,
  getLastNDays,
  startOfLocalDay,
  type StudySessionRow,
} from "@/lib/study-time";

type StudyTaskResource = {
  id: string;
  title: string;
} | null;

export type OfflineStudyTask = Tables<"study_tasks"> & {
  resource?: StudyTaskResource;
  deleted?: boolean;
  source: "remote" | "local";
  sync_status: "synced" | "pending";
};

export type OfflineStudySession = StudySessionRow & {
  resource_title: string | null;
  library_name: string | null;
  updated_at: string;
};

export type OfflineDailyGoalRecord = {
  id: string | null;
  user_id: string;
  daily_minutes_goal: number;
  current_streak: number;
  longest_streak: number;
  last_goal_met_on: string | null;
  updated_at: string;
  sync_status: "synced" | "pending";
};

type OfflineStudyJob =
  | {
    id: string;
    key: string;
    userId: string;
    type: "study-task-upsert";
    taskId: string;
  }
  | {
    id: string;
    key: string;
    userId: string;
    type: "study-task-delete";
    taskId: string;
  }
  | {
    id: string;
    key: string;
    userId: string;
    type: "daily-goal-upsert";
  };

type OfflineStudyUserState = {
  tasks: Record<string, OfflineStudyTask>;
  sessions: Record<string, OfflineStudySession>;
  goal: OfflineDailyGoalRecord | null;
  queue: OfflineStudyJob[];
};

type OfflineStudyState = {
  users: Record<string, OfflineStudyUserState>;
};

export type OfflineStudyPeriodSummary = {
  totalSeconds: number;
  averageSessionSeconds: number;
  activeDays: number;
  todaySeconds: number;
  chart: Array<{ key: string; label: string; seconds: number }>;
  resources: Array<{ resource_id: string | null; title: string; seconds: number }>;
  libraries: Array<{ library_id: string | null; name: string; seconds: number }>;
};

export type OfflineDailyGoalsState = {
  rowId: string | null;
  goalMinutes: number;
  goalSeconds: number;
  todaySeconds: number;
  progressPercent: number;
  currentStreak: number;
  longestStreak: number;
  goalMetToday: boolean;
  lastGoalMetOn: string | null;
  week: Array<{ key: string; label: string; seconds: number; metGoal: boolean }>;
};

const STORAGE_KEY = "athr.offline-study.v1";
const STORAGE_UPDATED_EVENT = "athr-offline-study-updated";
const DEFAULT_GOAL_MINUTES = 120;
const FRIDAY_START_DAY = 5;
const SESSION_RETENTION_DAYS = 180;

const createEmptyUserState = (): OfflineStudyUserState => ({
  tasks: {},
  sessions: {},
  goal: null,
  queue: [],
});

const readState = (): OfflineStudyState => {
  if (typeof window === "undefined") {
    return { users: {} };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { users: {} };
    }
    const parsed = JSON.parse(raw) as OfflineStudyState;
    return {
      users: parsed?.users ?? {},
    };
  } catch {
    return { users: {} };
  }
};

const emitStateChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STORAGE_UPDATED_EVENT));
};

const pruneOldSessions = (sessions: Record<string, OfflineStudySession>) => {
  const minTime = getLastNDays(SESSION_RETENTION_DAYS)[0].getTime();

  Object.entries(sessions).forEach(([sessionId, session]) => {
    if (new Date(session.started_at).getTime() < minTime) {
      delete sessions[sessionId];
    }
  });
};

const writeState = (state: OfflineStudyState) => {
  if (typeof window === "undefined") return;

  Object.values(state.users).forEach((userState) => {
    pruneOldSessions(userState.sessions);
  });

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    emitStateChanged();
  } catch {
    // Ignore storage failures and preserve in-memory flows.
  }
};

const mutateState = <T>(updater: (state: OfflineStudyState) => T): T => {
  const state = readState();
  const result = updater(state);
  writeState(state);
  return result;
};

const getUserState = (state: OfflineStudyState, userId: string): OfflineStudyUserState => {
  if (!state.users[userId]) {
    state.users[userId] = createEmptyUserState();
  }
  return state.users[userId];
};

const upsertQueueJob = (queue: OfflineStudyJob[], nextJob: OfflineStudyJob) => {
  const filtered = queue.filter((job) => job.key !== nextJob.key);
  filtered.push(nextJob);
  return filtered;
};

const removeQueueJobs = (queue: OfflineStudyJob[], keys: string[]) => {
  const blocked = new Set(keys);
  return queue.filter((job) => !blocked.has(job.key));
};

const getTaskUpsertKey = (taskId: string) => `study-task-upsert:${taskId}`;
const getTaskDeleteKey = (taskId: string) => `study-task-delete:${taskId}`;
const getDailyGoalKey = (userId: string) => `daily-goal-upsert:${userId}`;

const normalizeTask = (
  task: Tables<"study_tasks"> & { resource?: StudyTaskResource },
  source: OfflineStudyTask["source"],
  syncStatus: OfflineStudyTask["sync_status"],
): OfflineStudyTask => ({
  ...task,
  resource: task.resource ?? null,
  deleted: false,
  source,
  sync_status: syncStatus,
});

const mergeSessionRecord = (
  current: OfflineStudySession | undefined,
  incoming: OfflineStudySession,
): OfflineStudySession => ({
  ...incoming,
  duration_seconds: Math.max(current?.duration_seconds ?? 0, incoming.duration_seconds ?? 0),
  ended_at: incoming.ended_at ?? current?.ended_at ?? null,
  resource_title: incoming.resource_title ?? current?.resource_title ?? null,
  library_name: incoming.library_name ?? current?.library_name ?? null,
  updated_at: incoming.updated_at,
});

const sortWeekStartingOnFriday = <T extends { key: string }>(days: T[]) =>
  [...days].sort((left, right) => {
    const leftDay = (new Date(left.key).getDay() - FRIDAY_START_DAY + 7) % 7;
    const rightDay = (new Date(right.key).getDay() - FRIDAY_START_DAY + 7) % 7;
    return leftDay - rightDay;
  });

const computeGoalStreak = (dayTotals: Record<string, number>, goalSeconds: number, from = new Date()) => {
  let streak = 0;
  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);

  while ((dayTotals[getDateKey(cursor)] || 0) >= goalSeconds) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  return streak;
};

const computeLongestGoalStreak = (dayTotals: Record<string, number>, goalSeconds: number) => {
  const keys = Object.keys(dayTotals).sort();
  let best = 0;
  let current = 0;

  keys.forEach((key) => {
    if ((dayTotals[key] || 0) >= goalSeconds) {
      current += 1;
      best = Math.max(best, current);
      return;
    }
    current = 0;
  });

  return best;
};

export const getOfflineStudyEventName = () => STORAGE_UPDATED_EVENT;

export const getLocalStudyTasks = (userId: string, dateKey: string) => {
  const state = readState();
  const userState = state.users[userId];
  if (!userState) return [] as OfflineStudyTask[];

  return Object.values(userState.tasks)
    .filter((task) => task.user_id === userId && task.scheduled_date === dateKey && !task.deleted)
    .sort((left, right) => {
      if (left.sort_order !== right.sort_order) {
        return left.sort_order - right.sort_order;
      }
      return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
    });
};

export const getLocalStudyTask = (userId: string, taskId: string) => {
  const state = readState();
  return state.users[userId]?.tasks[taskId] ?? null;
};

export const hydrateStudyTasks = (
  userId: string,
  dateKey: string,
  tasks: Array<Tables<"study_tasks"> & { resource?: StudyTaskResource }>,
) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const incomingIds = new Set(tasks.map((task) => task.id));

    Object.entries(userState.tasks).forEach(([taskId, task]) => {
      if (
        task.scheduled_date === dateKey &&
        task.source === "remote" &&
        task.sync_status === "synced" &&
        !task.deleted &&
        !incomingIds.has(taskId)
      ) {
        delete userState.tasks[taskId];
      }
    });

    tasks.forEach((task) => {
      const existing = userState.tasks[task.id];
      if (existing?.deleted) return;
      if (existing?.sync_status === "pending") {
        userState.tasks[task.id] = {
          ...existing,
          resource: existing.resource ?? task.resource ?? null,
        };
        return;
      }

      userState.tasks[task.id] = normalizeTask(task, "remote", "synced");
    });
  });
};

export const createLocalStudyTask = ({
  userId,
  dateKey,
  title,
  resource,
}: {
  userId: string;
  dateKey: string;
  title: string;
  resource?: StudyTaskResource;
}) =>
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const visibleTasks = Object.values(userState.tasks).filter(
      (task) => task.scheduled_date === dateKey && !task.deleted,
    );
    const sortOrder = visibleTasks.length;
    const now = new Date().toISOString();
    const task = normalizeTask(
      {
        id: getSafeRandomId(),
        user_id: userId,
        title,
        resource_id: resource?.id ?? null,
        resource: resource ?? null,
        scheduled_date: dateKey,
        sort_order: sortOrder,
        is_completed: false,
        completed_at: null,
        created_at: now,
        updated_at: now,
      },
      "local",
      "pending",
    );

    userState.tasks[task.id] = task;
    userState.queue = upsertQueueJob(userState.queue, {
      id: getSafeRandomId(),
      key: getTaskUpsertKey(task.id),
      type: "study-task-upsert",
      userId,
      taskId: task.id,
    });

    return task;
  });

export const toggleLocalStudyTask = (userId: string, taskId: string) =>
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const current = userState.tasks[taskId];
    if (!current || current.deleted) return null;

    const nextCompleted = !current.is_completed;
    const nextTask: OfflineStudyTask = {
      ...current,
      is_completed: nextCompleted,
      completed_at: nextCompleted ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
      sync_status: "pending",
    };

    userState.tasks[taskId] = nextTask;
    userState.queue = upsertQueueJob(userState.queue, {
      id: getSafeRandomId(),
      key: getTaskUpsertKey(taskId),
      type: "study-task-upsert",
      userId,
      taskId,
    });

    return nextTask;
  });

export const deleteLocalStudyTask = (userId: string, taskId: string) =>
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const current = userState.tasks[taskId];
    if (!current) return null;

    if (current.source === "local" && current.sync_status === "pending") {
      delete userState.tasks[taskId];
      userState.queue = removeQueueJobs(userState.queue, [getTaskUpsertKey(taskId), getTaskDeleteKey(taskId)]);
      return taskId;
    }

    userState.tasks[taskId] = {
      ...current,
      deleted: true,
      updated_at: new Date().toISOString(),
      sync_status: "pending",
    };
    userState.queue = removeQueueJobs(userState.queue, [getTaskUpsertKey(taskId)]);
    userState.queue = upsertQueueJob(userState.queue, {
      id: getSafeRandomId(),
      key: getTaskDeleteKey(taskId),
      type: "study-task-delete",
      userId,
      taskId,
    });

    return taskId;
  });

export const getLocalStudySessions = (userId: string, options?: { since?: string }) => {
  const state = readState();
  const userState = state.users[userId];
  if (!userState) return [] as OfflineStudySession[];

  const minTime = options?.since ? new Date(options.since).getTime() : Number.NEGATIVE_INFINITY;
  return Object.values(userState.sessions)
    .filter((session) => session.user_id === userId && new Date(session.started_at).getTime() >= minTime)
    .sort((left, right) => new Date(right.started_at).getTime() - new Date(left.started_at).getTime());
};

export const upsertLocalStudySession = ({
  userId,
  session,
  resourceTitle,
  libraryName,
}: {
  userId: string;
  session: StudySessionRow;
  resourceTitle?: string | null;
  libraryName?: string | null;
}) =>
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const nextSession: OfflineStudySession = {
      ...session,
      resource_title: resourceTitle ?? userState.sessions[session.id]?.resource_title ?? null,
      library_name: libraryName ?? userState.sessions[session.id]?.library_name ?? null,
      updated_at: new Date().toISOString(),
    };
    userState.sessions[session.id] = mergeSessionRecord(userState.sessions[session.id], nextSession);
  });

export const hydrateStudySessions = ({
  userId,
  sessions,
}: {
  userId: string;
  sessions: Array<
    StudySessionRow & {
      resource?: { title?: string | null } | null;
      library?: { name?: string | null } | null;
    }
  >;
}) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    sessions.forEach((session) => {
      const nextSession: OfflineStudySession = {
        ...session,
        session_type: session.session_type as StudySessionRow["session_type"],
        resource_title: session.resource?.title ?? null,
        library_name: session.library?.name ?? null,
        updated_at: new Date().toISOString(),
      };
      userState.sessions[session.id] = mergeSessionRecord(userState.sessions[session.id], nextSession);
    });
  });
};

export const getLocalDailyGoalRecord = (userId: string) => {
  const state = readState();
  return state.users[userId]?.goal ?? null;
};

export const hydrateDailyGoal = (userId: string, goal: OfflineDailyGoalRecord | null) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    if (!goal) {
      if (userState.goal?.sync_status !== "pending") {
        userState.goal = null;
      }
      return;
    }

    if (userState.goal?.sync_status === "pending") {
      return;
    }

    userState.goal = {
      ...goal,
      sync_status: "synced",
    };
  });
};

export const upsertLocalDailyGoal = ({
  userId,
  rowId,
  goalMinutes,
  currentStreak,
  longestStreak,
  lastGoalMetOn,
}: {
  userId: string;
  rowId?: string | null;
  goalMinutes: number;
  currentStreak?: number;
  longestStreak?: number;
  lastGoalMetOn?: string | null;
}) =>
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const now = new Date().toISOString();
    const nextGoal: OfflineDailyGoalRecord = {
      id: rowId ?? userState.goal?.id ?? null,
      user_id: userId,
      daily_minutes_goal: Math.max(15, Math.floor(goalMinutes)),
      current_streak: Math.max(0, Math.floor(currentStreak ?? userState.goal?.current_streak ?? 0)),
      longest_streak: Math.max(0, Math.floor(longestStreak ?? userState.goal?.longest_streak ?? 0)),
      last_goal_met_on: lastGoalMetOn ?? userState.goal?.last_goal_met_on ?? null,
      updated_at: now,
      sync_status: "pending",
    };

    userState.goal = nextGoal;
    userState.queue = upsertQueueJob(userState.queue, {
      id: getSafeRandomId(),
      key: getDailyGoalKey(userId),
      type: "daily-goal-upsert",
      userId,
    });

    return nextGoal;
  });

const removeJobByKey = (queue: OfflineStudyJob[], key: string) => queue.filter((job) => job.key !== key);

const markStudyTaskSynced = (
  userId: string,
  taskId: string,
  task: Tables<"study_tasks"> & { resource?: StudyTaskResource },
) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    const current = userState.tasks[taskId];
    userState.queue = removeJobByKey(userState.queue, getTaskUpsertKey(taskId));
    if (!current) return;
    if (current.deleted) return;

    userState.tasks[taskId] = {
      ...normalizeTask(task, "remote", "synced"),
      resource: current.resource ?? task.resource ?? null,
    };
  });
};

const markStudyTaskDeleted = (userId: string, taskId: string) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    userState.queue = removeJobByKey(userState.queue, getTaskDeleteKey(taskId));
    delete userState.tasks[taskId];
  });
};

const markDailyGoalSynced = (
  userId: string,
  goal: Pick<OfflineDailyGoalRecord, "id" | "daily_minutes_goal" | "current_streak" | "longest_streak" | "last_goal_met_on">,
) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    userState.queue = removeJobByKey(userState.queue, getDailyGoalKey(userId));
    if (!userState.goal) return;

    userState.goal = {
      ...userState.goal,
      ...goal,
      sync_status: "synced",
      updated_at: new Date().toISOString(),
    };
  });
};

export const getOfflineStudyQueueCount = (userId?: string) => {
  const state = readState();
  if (userId) {
    return state.users[userId]?.queue.length ?? 0;
  }
  return Object.values(state.users).reduce((sum, userState) => sum + userState.queue.length, 0);
};

export const flushOfflineStudyQueue = async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return;
  }

  const jobs = Object.values(readState().users).flatMap((userState) => userState.queue);
  if (!jobs.length) {
    return;
  }

  for (const job of jobs) {
    try {
      if (job.type === "study-task-upsert") {
        const task = getLocalStudyTask(job.userId, job.taskId);
        if (!task || task.deleted) {
          markStudyTaskDeleted(job.userId, job.taskId);
          continue;
        }

        const { data, error } = await supabase
          .from("study_tasks")
          .upsert({
            id: task.id,
            user_id: task.user_id,
            title: task.title,
            resource_id: task.resource_id,
            scheduled_date: task.scheduled_date,
            sort_order: task.sort_order,
            is_completed: task.is_completed,
            completed_at: task.completed_at,
          })
          .select("*, resource:resources(id,title)")
          .single();

        if (error) {
          continue;
        }

        markStudyTaskSynced(job.userId, job.taskId, data as Tables<"study_tasks"> & { resource?: StudyTaskResource });
        continue;
      }

      if (job.type === "study-task-delete") {
        const { error } = await supabase
          .from("study_tasks")
          .delete()
          .eq("id", job.taskId)
          .eq("user_id", job.userId);

        if (error) {
          continue;
        }

        markStudyTaskDeleted(job.userId, job.taskId);
        continue;
      }

      const goal = getLocalDailyGoalRecord(job.userId);
      if (!goal) {
        markDailyGoalSynced(job.userId, {
          id: null,
          daily_minutes_goal: DEFAULT_GOAL_MINUTES,
          current_streak: 0,
          longest_streak: 0,
          last_goal_met_on: null,
        });
        continue;
      }

      const { data, error } = await supabase
        .from("daily_goals" as never)
        .upsert(
          {
            user_id: goal.user_id,
            daily_minutes_goal: goal.daily_minutes_goal,
            current_streak: goal.current_streak,
            longest_streak: goal.longest_streak,
            last_goal_met_on: goal.last_goal_met_on || null, // Ensure undefined is null
          } as never,
          { onConflict: "user_id" },
        )
        .select("id,daily_minutes_goal,current_streak,longest_streak,last_goal_met_on")
        .single();

      if (error) {
        continue;
      }

      markDailyGoalSynced(job.userId, {
        id: (data as { id: string }).id,
        daily_minutes_goal: (data as { daily_minutes_goal: number }).daily_minutes_goal,
        current_streak: (data as { current_streak: number }).current_streak,
        longest_streak: (data as { longest_streak: number }).longest_streak,
        last_goal_met_on: (data as { last_goal_met_on: string | null }).last_goal_met_on,
      });
    } catch {
      // Leave the job queued for the next retry.
    }
  }
};

export const buildOfflineDailyGoalsState = (userId: string): OfflineDailyGoalsState => {
  const goalRow = getLocalDailyGoalRecord(userId);
  const sessions = getLocalStudySessions(userId, {
    since: getLastNDays(60)[0].toISOString(),
  });
  const dayTotals = aggregateSessionsByDay(sessions);
  const goalMinutes = Math.max(15, Number(goalRow?.daily_minutes_goal || DEFAULT_GOAL_MINUTES));
  const goalSeconds = goalMinutes * 60;
  const todayKey = getDateKey(new Date());
  const todaySeconds = dayTotals[todayKey] || 0;
  const currentStreak = computeGoalStreak(dayTotals, goalSeconds);
  const longestStreak = Math.max(goalRow?.longest_streak || 0, computeLongestGoalStreak(dayTotals, goalSeconds));
  const lastGoalMetOn =
    Object.keys(dayTotals)
      .sort()
      .reverse()
      .find((key) => (dayTotals[key] || 0) >= goalSeconds) ?? goalRow?.last_goal_met_on ?? null;

  return {
    rowId: goalRow?.id ?? null,
    goalMinutes,
    goalSeconds,
    todaySeconds,
    progressPercent: Math.min(100, Math.round((todaySeconds / goalSeconds) * 100)),
    currentStreak,
    longestStreak,
    goalMetToday: todaySeconds >= goalSeconds,
    lastGoalMetOn,
    week: sortWeekStartingOnFriday(
      getLastNDays(7).map((day) => {
        const key = getDateKey(day);
        const seconds = dayTotals[key] || 0;
        return {
          key,
          label: day.toLocaleDateString(undefined, { weekday: "short" }),
          seconds,
          metGoal: seconds >= goalSeconds,
        };
      }),
    ),
  };
};

export const buildOfflineStudyPeriodSummary = (userId: string, periodDays: number): OfflineStudyPeriodSummary => {
  const sinceDate = getLastNDays(periodDays)[0];
  const sessions = getLocalStudySessions(userId, { since: sinceDate.toISOString() });
  const byDay = aggregateSessionsByDay(sessions);
  const chartDays = getLastNDays(Math.min(periodDays, 14));
  const totalSeconds = sessions.reduce((sum, row) => sum + Number(row.duration_seconds || 0), 0);
  const averageSessionSeconds = sessions.length > 0 ? Math.round(totalSeconds / sessions.length) : 0;

  const resourceMap = new Map<string, { resource_id: string | null; title: string; seconds: number }>();
  const libraryMap = new Map<string, { library_id: string | null; name: string; seconds: number }>();

  sessions.forEach((session) => {
    const resourceKey = session.resource_id || "unknown";
    const libraryKey = session.library_id || "unknown";

    if (!resourceMap.has(resourceKey)) {
      resourceMap.set(resourceKey, {
        resource_id: session.resource_id,
        title: session.resource_title || "Untitled resource",
        seconds: 0,
      });
    }

    if (!libraryMap.has(libraryKey)) {
      libraryMap.set(libraryKey, {
        library_id: session.library_id,
        name: session.library_name || "Unknown library",
        seconds: 0,
      });
    }

    resourceMap.get(resourceKey)!.seconds += Number(session.duration_seconds || 0);
    libraryMap.get(libraryKey)!.seconds += Number(session.duration_seconds || 0);
  });

  return {
    totalSeconds,
    averageSessionSeconds,
    activeDays: Object.values(byDay).filter((value) => value > 0).length,
    todaySeconds: byDay[getDateKey(startOfLocalDay())] || 0,
    chart: chartDays.map((day) => ({
      key: getDateKey(day),
      label: day.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      seconds: byDay[getDateKey(day)] || 0,
    })),
    resources: Array.from(resourceMap.values()).sort((left, right) => right.seconds - left.seconds).slice(0, 6),
    libraries: Array.from(libraryMap.values()).sort((left, right) => right.seconds - left.seconds).slice(0, 6),
  };
};
